import { Check, Copy, Download, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";

import { BrandLogo } from "@/components/brand-logo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useGenerateImage, useLogout, useMe } from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { autoDetectFromStructured, generate, refinePrompt } from "./engine";
import { ASPECT_RATIOS, MODEL_GROUPS, MODELS } from "./models";
import { parse } from "./parser";
import { type TemplateKey, TEMPLATES } from "./templates";

const PROJECT_NAME = "On-Brand Picture Generator";

type Mode = "structured" | "freetext";

const MOODS = [
  { value: "none", label: "Template default" },
  { value: "warm-optimistic", label: "Warm & optimistic" },
  { value: "gritty-authentic", label: "Gritty & authentic" },
  { value: "serene-contemplative", label: "Serene & contemplative" },
  { value: "energetic-dynamic", label: "Energetic & dynamic" },
  { value: "intimate-quiet", label: "Intimate & quiet" },
];

const TEMPLATE_KEYS = Object.keys(TEMPLATES) as TemplateKey[];

const CONFIDENCE_STYLES = {
  high: "text-success",
  medium: "text-accent",
  low: "text-muted",
} as const;

function errorMessage(error: unknown): string {
  if (error && typeof error === "object" && "body" in error) {
    const body = (error as { body: unknown }).body;
    if (body && typeof body === "object" && "detail" in body) {
      const detail = (body as { detail: unknown }).detail;
      if (typeof detail === "string") {
        return detail;
      }
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Something went wrong generating the image.";
}

function TemplateSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="auto">Auto-detect</SelectItem>
        {TEMPLATE_KEYS.map((key) => (
          <SelectItem key={key} value={key}>
            {TEMPLATES[key].name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function GeneratorPage() {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const { data: me } = useMe();
  const imageMutation = useGenerateImage();

  const [mode, setMode] = useState<Mode>("structured");

  const [template, setTemplate] = useState("auto");
  const [subject, setSubject] = useState("");
  const [action, setAction] = useState("");
  const [location, setLocation] = useState("");
  const [element, setElement] = useState("");
  const [mood, setMood] = useState("none");

  const [freeText, setFreeText] = useState("");
  const [debouncedText, setDebouncedText] = useState("");
  const [templateOverride, setTemplateOverride] = useState("auto");

  const [prompt, setPrompt] = useState("");
  const [promptTemplate, setPromptTemplate] = useState<TemplateKey | null>(null);
  const [copied, setCopied] = useState(false);

  const [modelId, setModelId] = useState("fal-ai/flux-pro/v1.1-ultra");
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [imageUrl, setImageUrl] = useState("");
  const [imageModel, setImageModel] = useState("");
  const [imageError, setImageError] = useState("");

  const [feedback, setFeedback] = useState("");
  const [iteration, setIteration] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedText(freeText.trim()), 300);
    return () => clearTimeout(timer);
  }, [freeText]);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const parsed = useMemo(() => (debouncedText ? parse(debouncedText) : null), [debouncedText]);

  const autoDetectHint = useMemo(() => {
    if (template !== "auto") {
      return "";
    }
    const inputs = { subject, action, location, element };
    if (!subject.trim() && !action.trim() && !location.trim() && !element.trim()) {
      return "";
    }
    const detection = autoDetectFromStructured(inputs);
    return `Detected: ${TEMPLATES[detection.templateKey].name}`;
  }, [template, subject, action, location, element]);

  const isGenerating = imageMutation.isPending;

  const showPrompt = (nextPrompt: string, key: TemplateKey) => {
    setPrompt(nextPrompt);
    setPromptTemplate(key);
    setIteration(0);
    setFeedback("");
  };

  const handleGenerateStructured = () => {
    const inputs = {
      subject: subject.trim(),
      action: action.trim(),
      location: location.trim(),
      element: element.trim(),
      mood: mood === "none" ? "" : mood,
    };
    const key =
      template === "auto"
        ? autoDetectFromStructured(inputs).templateKey
        : (template as TemplateKey);
    showPrompt(generate(key, inputs), key);
  };

  const handleGenerateFreeText = () => {
    const text = freeText.trim();
    if (!text) {
      return;
    }
    const result = parse(text);
    const key =
      templateOverride === "auto" ? result.templateKey : (templateOverride as TemplateKey);
    showPrompt(
      generate(key, {
        subject: result.subject,
        action: result.action,
        location: result.location,
        element: result.element,
      }),
      key,
    );
  };

  const handleCopy = async () => {
    if (!prompt) {
      return;
    }
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
    } catch (error) {
      logger.error("[generator] copy failed", error);
    }
  };

  const runImageGeneration = (promptToUse: string, onSuccess?: () => void) => {
    setImageError("");
    imageMutation.mutate(
      { prompt: promptToUse, modelId, aspectRatio },
      {
        onSuccess: (result) => {
          setImageUrl(result.image_url);
          setImageModel(result.model);
          onSuccess?.();
        },
        onError: (error) => {
          logger.error("[generator] image generation failed", error);
          setImageError(errorMessage(error));
        },
      },
    );
  };

  const handleGenerateImage = () => {
    if (!prompt || isGenerating) {
      return;
    }
    runImageGeneration(prompt);
  };

  const handleRefine = () => {
    const trimmed = feedback.trim();
    if (!trimmed || !prompt || isGenerating) {
      return;
    }
    const refined = refinePrompt(prompt, trimmed);
    setPrompt(refined);
    runImageGeneration(refined, () => {
      setIteration((count) => count + 1);
      setFeedback("");
    });
  };

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  const modelName = MODELS[modelId]?.name ?? modelId;

  return (
    <div className="bg-background min-h-screen">
      {/* Dark branded header */}
      <header className="bg-primary text-primary-foreground px-6 py-4">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <BrandLogo name={PROJECT_NAME} className="h-7 brightness-0 invert" />
          <div className="flex items-center gap-2">
            <Link to="/profile">
              <Button
                variant="ghost"
                size="sm"
                className="text-primary-foreground/80 hover:bg-white/10 hover:text-white"
              >
                Profile
              </Button>
            </Link>
            {me?.role === "admin" && (
              <>
                <Link to="/admin/users">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary-foreground/80 hover:bg-white/10 hover:text-white"
                  >
                    Admin
                  </Button>
                </Link>
                <Link to="/admin/activity">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary-foreground/80 hover:bg-white/10 hover:text-white"
                  >
                    Activity
                  </Button>
                </Link>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-white/20 bg-transparent text-white hover:bg-white/10"
            >
              Sign out
            </Button>
          </div>
        </div>
      </header>

      {/* Hero section */}
      <div className="bg-primary px-6 pt-8 pb-12">
        <div className="mx-auto max-w-5xl">
          <p className="text-sm font-medium tracking-[0.06em] text-white/50 uppercase">
            Board of Innovation
          </p>
          <h1 className="mt-2 text-3xl text-white md:text-4xl">On-Brand Picture Generator</h1>
          <p className="mt-3 max-w-xl text-base text-white/70">
            Create visuals that stay unmistakably on-brand. Build structured prompts from BOI
            photography templates, then generate images directly.
          </p>
        </div>
      </div>

      <main className="mx-auto max-w-5xl px-6 pb-16">
        {/* Content pulled up into the hero with negative margin */}
        <div className="-mt-6 space-y-6">
          {/* Mode toggle */}
          <div className="border-border flex gap-1 rounded-md border bg-white p-1">
            <button
              type="button"
              onClick={() => setMode("structured")}
              className={cn(
                "flex-1 rounded-sm px-4 py-2.5 text-sm font-medium transition-colors",
                mode === "structured"
                  ? "bg-primary text-white"
                  : "text-muted hover:text-foreground",
              )}
            >
              Structured
            </button>
            <button
              type="button"
              onClick={() => setMode("freetext")}
              className={cn(
                "flex-1 rounded-sm px-4 py-2.5 text-sm font-medium transition-colors",
                mode === "freetext" ? "bg-primary text-white" : "text-muted hover:text-foreground",
              )}
            >
              Free-text
            </button>
          </div>

          {/* Input panel */}
          {mode === "structured" ? (
            <Card className="border-border border p-6">
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="template-select">Template</Label>
                  <TemplateSelect id="template-select" value={template} onChange={setTemplate} />
                  {autoDetectHint && (
                    <p className="text-accent text-xs font-medium">{autoDetectHint}</p>
                  )}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="subject-input">Subject</Label>
                    <Input
                      id="subject-input"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="e.g. a cyclist in a worn jersey"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="action-input">Action</Label>
                    <Input
                      id="action-input"
                      value={action}
                      onChange={(e) => setAction(e.target.value)}
                      placeholder="e.g. climbing a gravel road"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location-input">Location</Label>
                    <Input
                      id="location-input"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="e.g. rolling hills at sunrise"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="element-input">Element / detail</Label>
                    <Input
                      id="element-input"
                      value={element}
                      onChange={(e) => setElement(e.target.value)}
                      placeholder="e.g. dust kicked up by the tires"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mood-select">Mood</Label>
                  <Select value={mood} onValueChange={setMood}>
                    <SelectTrigger id="mood-select" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MOODS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleGenerateStructured}
                  className="bg-accent text-accent-foreground hover:bg-[#0E0E98]"
                >
                  Generate Prompt
                </Button>
              </div>
            </Card>
          ) : (
            <Card className="border-border border p-6">
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="freetext-input">Describe your image</Label>
                  <Textarea
                    id="freetext-input"
                    value={freeText}
                    onChange={(e) => setFreeText(e.target.value)}
                    rows={4}
                    placeholder="e.g. A cyclist riding through a sunlit canyon at golden hour"
                  />
                </div>
                {parsed && (
                  <div className="bg-background grid grid-cols-2 gap-3 rounded-lg p-4 text-sm md:grid-cols-4">
                    {(
                      [
                        ["Subject", parsed.subject],
                        ["Action", parsed.action],
                        ["Location", parsed.location],
                      ] as const
                    ).map(([label, value]) => (
                      <div key={label}>
                        <p className="text-muted text-[11px] font-medium tracking-wider uppercase">
                          {label}
                        </p>
                        <p className="text-foreground mt-0.5">
                          {value || <em className="text-muted">not detected</em>}
                        </p>
                      </div>
                    ))}
                    <div>
                      <p className="text-muted text-[11px] font-medium tracking-wider uppercase">
                        Template
                      </p>
                      <p className={cn("mt-0.5 font-medium", CONFIDENCE_STYLES[parsed.confidence])}>
                        {TEMPLATES[parsed.templateKey].name}
                      </p>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="template-override">Template override</Label>
                  <TemplateSelect
                    id="template-override"
                    value={templateOverride}
                    onChange={setTemplateOverride}
                  />
                </div>
                <Button
                  onClick={handleGenerateFreeText}
                  disabled={!freeText.trim()}
                  className="bg-accent text-accent-foreground hover:bg-[#0E0E98]"
                >
                  Generate Prompt
                </Button>
              </div>
            </Card>
          )}

          {/* Prompt output */}
          <Card className="border-border border p-6">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-foreground text-lg">Prompt</h2>
              <div className="flex items-center gap-2">
                {promptTemplate && (
                  <span className="bg-accent/8 text-accent rounded-full px-3 py-1 text-xs font-medium">
                    {TEMPLATES[promptTemplate].name}
                  </span>
                )}
                <Button variant="outline" size="sm" onClick={handleCopy} disabled={!prompt}>
                  {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>
            <div className="bg-background mt-3 min-h-20 rounded-lg p-4 text-sm leading-relaxed">
              {prompt ? (
                <p className="text-foreground">{prompt}</p>
              ) : (
                <p className="text-muted italic">Your generated prompt will appear here.</p>
              )}
            </div>
          </Card>

          {/* Image generation */}
          <Card className="border-border border p-6">
            <h2 className="text-foreground text-lg">Generate Image</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="model-select">Model</Label>
                <Select value={modelId} onValueChange={setModelId}>
                  <SelectTrigger id="model-select" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODEL_GROUPS.map((group) => (
                      <SelectGroup key={group.label}>
                        <SelectLabel>{group.label}</SelectLabel>
                        {group.models.map((id) => (
                          <SelectItem key={id} value={id}>
                            {MODELS[id]?.name ?? id}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="aspect-ratio-select">Aspect ratio</Label>
                <Select value={aspectRatio} onValueChange={setAspectRatio}>
                  <SelectTrigger id="aspect-ratio-select" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASPECT_RATIOS.map((ratio) => (
                      <SelectItem key={ratio.value} value={ratio.value}>
                        {ratio.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <Button
                onClick={handleGenerateImage}
                disabled={!prompt || isGenerating}
                className="bg-accent text-accent-foreground hover:bg-[#0E0E98]"
              >
                {isGenerating && <Loader2 className="size-4 animate-spin" />}
                {isGenerating ? "Generating..." : "Generate Image"}
              </Button>
            </div>

            {isGenerating && (
              <div
                className="bg-accent/5 mt-4 flex items-center gap-3 rounded-lg px-4 py-3"
                role="status"
              >
                <Loader2 className="text-accent size-4 animate-spin" />
                <p className="text-sm">Generating image with {modelName}...</p>
              </div>
            )}
            {imageError && (
              <div className="bg-destructive/5 mt-4 rounded-lg px-4 py-3" role="alert">
                <p className="text-destructive text-sm">{imageError}</p>
              </div>
            )}
            {imageUrl && (
              <div className="mt-5 space-y-3">
                <div className="overflow-hidden rounded-lg">
                  <img src={imageUrl} alt="Generated result" className="w-full" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted text-xs">{imageModel}</span>
                  <a
                    href={imageUrl}
                    download="generated-image.png"
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                  >
                    <Download className="size-3.5" />
                    Download
                  </a>
                </div>
              </div>
            )}
          </Card>

          {/* Feedback / refine */}
          {imageUrl && (
            <Card className="border-border border p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-foreground text-lg">Refine</h2>
                {iteration > 0 && (
                  <span className="text-muted text-xs">
                    Iteration {iteration} &mdash; {iteration} refinement
                    {iteration > 1 ? "s" : ""} applied
                  </span>
                )}
              </div>
              <div className="mt-3 space-y-3">
                <Label htmlFor="feedback-input">What should change?</Label>
                <Textarea
                  id="feedback-input"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={3}
                  placeholder="e.g. Warmer light, subject further from the camera"
                />
                <Button
                  onClick={handleRefine}
                  disabled={!feedback.trim() || isGenerating}
                  className="bg-accent text-accent-foreground hover:bg-[#0E0E98]"
                >
                  {isGenerating ? "Refining..." : "Refine & Regenerate"}
                </Button>
              </div>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
